import {
  Accordion,
  Box,
  Button,
  ButtonsGroup,
  Card,
  Grid,
  Heading,
  InputSelect,
  InputText,
  Maps,
  PageHeader,
  PageWrapper,
  Select,
  Typo
} from "#/atoms";
import { AddPostcodeModal, ChangePasswordModal, ViewPhotoModal } from "@/components/organisms";
import { DESKTOP_SPACING, HPOSITION, NATURAL } from "@/theme";
import { FC, memo, useState } from "react";
import { HeadingIcon, RadioButtonGroup } from "#/molecules";
import { useInput, useInputSelect, useMaps, useModal, useSelect } from "@/hooks";

import { NotesCard } from "../Order/Cards";
import { RadioButtonProps } from "@/components/molecules/RadioButtonGroup/RadioButtonGroup.interface";
import { WorkerDetailsProps } from "./WorkerDetails.interface";

const WorkerDetails: FC<WorkerDetailsProps> = ({
  googleCalendarIds,
  isAdmin,
  notes,
  onAddNote,
  onAddPostCode,
  onCancelNote,
  onDeletePostCode,
  onSaveNote,
  onChangePassword,
  onSaveAccountDetails,
  onSaveDetails,
  onSaveServices,
  onSaveBankDetails,
  onLinkGoogleCalendar,
  onUnlinkGoogleCalendar,
  owner,
  workerData,
  workerId,
  workerPortfolio,
  workerServices }) => {
  const { mapsAddress, onBlur, onChange } = useMaps();

  const { showModal, onCloseModal, onOpenModal } = useModal(); 
  const { showModal: showPostcodeModal, onCloseModal: onClosePostcodeModal, onOpenModal: onOpenPostcodeModal } = useModal(); 
  const { showModal: showChangePasswordModal, onCloseModal: onCloseChangePasswordModal, onOpenModal: onOpenChangePasswordModal } = useModal(); 

  const [img, setImg] = useState<string>();
  
  const login = useInput(workerData?.accountDetails.login);
  const email = useInput(workerData?.accountDetails.email);
  const accountPhone = useInput(workerData?.accountDetails.accountPhoneNumber);
  const firstName = useInput(workerData?.accountDetails.firstName);
  const lastName = useInput(workerData?.accountDetails.lastName);
  const daysToPay = useInput((workerData?.accountDetails.daysToPayInvoices ?? 0).toString());

  const phoneNumber = useInput(workerData?.workerDetails.phoneNumber);
  const houseName = useInput(workerData?.workerDetails.houseName);
  const houseNumber = useInput(workerData?.workerDetails.houseNumber);
  const road = useInput(workerData?.workerDetails.road);
  const area = useInput(workerData?.workerDetails.area);
  const town = useInput(workerData?.workerDetails.town);
  const county = useInput(workerData?.workerDetails.county);
  const postcode = useInput(workerData?.workerDetails.postCode);
  const addressNotes = useInput(workerData?.workerDetails.notes);
  const fullTime = useInputSelect('fullTime', workerData?.workerDetails.fullTimeType ?? false);
  const commutingDistance = useInput(workerData?.workerDetails.commutingDistance);
  const commutingDistanceType = useInput(`${workerData?.workerDetails.commutingDistanceType}` || '');
  const selectedGoogleCalendarId = useSelect(workerData?.workerDetails.googleCalendarId || '');
  const websiteLink = useInput(workerData?.workerDetails.websiteLink);
   
  const bankAccountName = useInput(workerData?.bankDetails.bankAccountName);
  const bankAccountNumber = useInput(workerData?.bankDetails.bankAccountNumber);
  const bankName = useInput(workerData?.bankDetails.bankName);
  const bankSortCode = useInput(workerData?.bankDetails.bankSortCode);

  const [services, setServices] = useState(workerServices);

  const commutingDistanceTypes : RadioButtonProps[] = [{
      id: "1",
      label: "Kilometres",
      value: "0"
    },
    {
      id: "2",
      label: "Miles",
      value: "1"
    }
  ]  
  
  const handleChangePassword = async (oldPassword: string, newPassword: string, confirmPassword: string ) => {
    onChangePassword(oldPassword, newPassword, confirmPassword);
    onCloseChangePasswordModal();
  }

  const handleCommutingDistanceTypeChange = (groupName: string, selectedValue: string) => {
    commutingDistanceType.setValue(selectedValue);
  };

  const handleSaveAccountDetails = async () => {
    onSaveAccountDetails({
      AccountPhoneNumber: accountPhone.value,
      CountryId: 1,
      DaysToPayInvoices: parseInt(daysToPay.value, 10),
      DefaultPhotos: 0,
      Email: email.value,
      FirstName: firstName.value,
      Id: workerId,
      LastName: lastName.value,
      UserName: login.value,
      UserUpsellPrice: 0,
      Website: websiteLink.value
    });
  };

  const handleSaveDetails = async () => {
    onSaveDetails({
      AddressId: 0,
      AddressString: null,
      Area: area.value,
      CommutingDistance: commutingDistance.value,
      CommutingDistanceType: commutingDistanceType.value,
      Country: county.value,
      DoorNumber: null,
      Flat: null,
      FullTimeType: fullTime.checked,
      GoogleCalendarId: selectedGoogleCalendarId.value,
      HouseName: houseName.value,
      HouseNumber: houseNumber.value,
      Notes: "",
      PhoneNumber: phoneNumber.value,
      PostCode: postcode.value,
      Rating: 0,
      Road: road.value,
      Town: town.value,
      WebsiteLink: websiteLink.value
    })
  };

  const handleCheckedChange = async (id: string, checked: boolean) =>{
    setServices((prev) =>
      prev.map((p) => (p.id === id ? {...p, checked } : p))
  );
  }

  const handleSaveServices = async () => {
    onSaveServices(services.map((w) => { return {
      Checked: w.checked,
      Name: w.name,
      OrderTypeId: w.id
    } }));
  }

  const handleAddPostCode = () => {
    onOpenPostcodeModal();
  };

  const handleSaveBankDetails = () => {
    onSaveBankDetails({
      BankAccountName: bankAccountName.value,
      BankAccountNumber: bankAccountNumber.value,
      BankName: bankName.value,
      BankSortCode: bankSortCode.value
    })
  }

  const handleCopyToClipboard = async() => {
    const text = "Bank Account Name: " + bankAccountName.value
                        + ", Sort Code: " + bankSortCode.value
                        + ", Bank Account Number: " + bankAccountNumber.value
                        + ", Bank Name: " + bankName.value
                        navigator.clipboard.writeText(text).then(() => {
                            //l.info("{{customers.bankDetails.bankInformationCopied}}");
                        });
  }

  const handleZoom = (imgUrl: string) => {
    setImg(imgUrl);
    onOpenModal();
  }

  const handleSavePostCode = (pcode: string, pcodeArea: string) => {
    onAddPostCode(pcodeArea ? pcodeArea.split(' ')[0] : pcode);
    onClosePostcodeModal();
  }

  return (
    <>
     { workerData && <PageWrapper pb="100px">
        <PageHeader>
          <Heading scale="h1" fontColor={NATURAL.K}>Edit Worker Profile</Heading>
        </PageHeader>

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="contacts" title="Account Details" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(3, 30%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                <InputText type="text" label="Login" value={login.value} onChange={login.onChange} />
                <InputText type="email" label="E-mail Address" value={email.value} onChange={email.onChange} />
                <InputText type="tel" label="Account Phone Number" value={accountPhone.value} onChange={accountPhone.onChange} />
                <InputText type="text" label="First Name" value={firstName.value} onChange={firstName.onChange} />
                <InputText type="text" label="Last Name" value={lastName.value} onChange={lastName.onChange} />
              </Grid>
              <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
                <Button onClick={handleSaveAccountDetails}>
                  Save
                </Button>
                <Button onClick={onOpenChangePasswordModal}>
                  Change Password
                </Button>
              </ButtonsGroup>
            </Accordion>
          </Box>
        </Card>

        <Card padding="0">
          <Grid gridTemplateColumns="repeat(2, 1fr)">
            <Box padding={`${DESKTOP_SPACING.EL}px`}>
              <HeadingIcon
                icon="location"
                title="Address Details"
              />
              <InputText type="text" label="Phone Number" value={phoneNumber.value} onChange={phoneNumber.onChange} />
              <InputText type="text" label="House Name" value={houseName.value} onChange={houseName.onChange} />
              <InputText type="text" label="Number" value={houseNumber.value} onChange={houseNumber.onChange} />
              <InputText type="text" label="Road" value={road.value} onChange={road.onChange} />
              <InputText type="text" label="Area" value={area.value} onChange={area.onChange} />
              <InputText type="text" label="Town" value={town.value} onChange={town.onChange} />
              <InputText type="text" label="County" value={county.value} onChange={county.onChange} />
              <InputText type="text" label="Postcode" value={postcode.value} onBlur={onBlur} onChange={postcode.onChange} />
              <InputText type="text" label="Notes" value={addressNotes.value} onBlur={onBlur} onChange={onChange} />
              <InputText type="number" label="Commuting Distance" value={commutingDistance.value} onBlur={onBlur} onChange={commutingDistance.onChange} />
              <RadioButtonGroup groupName="cancelFeeType" inputs={commutingDistanceTypes} selectedValue={commutingDistanceType.value} onChangeValue={handleCommutingDistanceTypeChange} />

              <InputText type="url" label="Website Link" value={websiteLink.value} onChange={websiteLink.onChange} />
              <InputSelect type="checkbox" id="fullTime" name="fullTime" checked={fullTime.checked} label="Full Time Photographer" value={fullTime.value} onChangeValue={fullTime.onChange} />
              <Box style={{margin: '19px 0px 5px'}}>
                {!isAdmin &&  workerData?.workerDetails.googleCalendarId &&<> 
                    <Select label="Google Calendars"
                          options={googleCalendarIds.map((k) => { return { id: k.Id, label: k.Name} })}
                          onChange={selectedGoogleCalendarId.onChange}
                          selected={selectedGoogleCalendarId.value}
                        />
                        <Button onClick={onUnlinkGoogleCalendar}>Unlink Google Calendar</Button>
                        </> }
                
                {!isAdmin && !(workerData?.workerDetails.googleCalendarId) && <>
                <Typo>Google Calendar is not enabled right now.</Typo>
                <Button onClick={onLinkGoogleCalendar}>Google Calendar Sync</Button>
                </> }
              </Box>
              <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
                <Button onClick={() => handleSaveDetails() }>
                  Save
                </Button>
              </ButtonsGroup>
            </Box>
            <Box>
              <Maps address={mapsAddress} />
            </Box>
          </Grid>
        </Card>

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="services" title="Services" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              { services.map((service) => (<InputSelect 
              key={service.id} 
              label={service.name} 
              type="checkbox" 
              checked={service.checked} 
              value={service.name}
              onChangeValue={(checked) => { handleCheckedChange(service.id, checked) }}
             />) ) }
             <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
              <Button onClick={handleSaveServices}>
                Save
              </Button>
            </ButtonsGroup>
            </Accordion>
          </Box>
        </Card>

        <Card width="49%" padding="0" display="inline-block">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="wallet" title="Bank Details" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
             <Grid columnGap={DESKTOP_SPACING.XL} gridTemplateColumns="repeat(1, 100%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                <InputText type="text" label="Bank Account Name" value={bankAccountName.value} onChange={bankAccountName.onChange} />
                <InputText type="text" label="Sort Code" value={bankSortCode.value} onChange={bankSortCode.onChange} />
                <InputText type="text" label="Account Number" value={bankAccountNumber.value} onChange={bankAccountNumber.onChange} />
                <InputText type="text" label="Bank Name" value={bankName.value} onChange={bankName.onChange} />
              </Grid>
             <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
              <Button onClick={() => handleCopyToClipboard()}>
                Copy to Clipboard
              </Button>
              <Button onClick={() => handleSaveBankDetails()}>
                Save
              </Button>
            </ButtonsGroup>
            </Accordion>
          </Box>
        </Card>

        <Card width="49%"  padding="0" marginLeft="10px" display="inline-block">
          <Box padding={`${DESKTOP_SPACING.EL}px`} display="inline-block" position="relative">
            <Accordion
              header={<HeadingIcon icon="wallet" title="Post Codes" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
             { workerData.postcodes.map(post => {return (<div key={post.workerPostcodeId} style={{ borderBottom: '1px solid #DDDDDD', display: 'flex', justifyContent: 'space-between', margin: '10px', padding:'5px'}} ><span>{post.postCode}</span><Button actionType="outline" onClick={() => onDeletePostCode(post.workerPostcodeId)}>Delete</Button></div>)}) }
             <ButtonsGroup align={HPOSITION.RIGHT} fullWidth>
              <Button onClick={() => handleAddPostCode()}>
                Add New
              </Button>
            </ButtonsGroup>
            </Accordion>
          </Box>
        </Card>

        { isAdmin && <NotesCard
          notes={notes}
          onAddNote={onAddNote}
          onCancelNote={onCancelNote}
          onSaveNote={onSaveNote}
          owner={owner}
        /> }

        <Card padding="0">
          <Box padding={`${DESKTOP_SPACING.EL}px`}>
            <Accordion
              header={<HeadingIcon icon="services" title="Portfolio" />}
              onToggle={() => null}
              showArrow
              state="open"
            >
              <Grid columnGap={DESKTOP_SPACING.LG} gridTemplateColumns="repeat(3, 30%)" margin={`${DESKTOP_SPACING.XXL}px 0 0`}>
                { workerPortfolio.map((portfolio) => (<div key={portfolio.uploadFileId} style={{padding: '0px 15px 15px 0px'}}>
                  <div style={{ backgroundColor: '#DDDDDD', backgroundImage: `url('${portfolio.thumbURL}')`,backgroundPosition: 'center center' , backgroundRepeat: 'no-repeat', backgroundSize: 'contain', height: '300px' }}></div>
                  <ButtonsGroup fullWidth margin="10px">
                     <Button onClick={() => handleZoom(portfolio.fullURL)}>ZOOM</Button>
                  </ButtonsGroup>
                  </div>) ) }
              </Grid>
            </Accordion>
          </Box>
        </Card>
       </PageWrapper> }
       {showModal && img && <ViewPhotoModal
          imageUrl={img}
          onClose={onCloseModal}
        />
        }
        {showPostcodeModal && <AddPostcodeModal
          onClose={onClosePostcodeModal}
          onAddPostcode={handleSavePostCode}
        />
        }
        {showChangePasswordModal  && <ChangePasswordModal
          onClose={onCloseChangePasswordModal}
          onChangePassword={handleChangePassword}
          />
        }
    </>
  );
};

export default memo(WorkerDetails);
